import { getMaterialReceipts } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { teamLabel } from './rbac';
import type { ExportParams } from './exportWeeklyReport';

export interface CountEntry {
  label: string;
  count: number;
}

export interface FloorControlData {
  exceptionsByStatus: CountEntry[];
  escalationsByTeam: CountEntry[];
  intakeByOperator: CountEntry[];
  oversByStatus: CountEntry[];
}

export interface AuditTrailEntry {
  ts: string;
  actor: string;
  action: string;
  target: string;
  details: string;
}

export interface OpsReportData extends ExportParams {
  floorControl: FloorControlData;
  auditTrail: AuditTrailEntry[];
}

const EXCEPTION_STATUSES = ['No Job Found', 'Received Short', 'On Hold', 'Follow Ups'];

function countBy(rows: MaterialReceiptRow[], keyFn: (row: MaterialReceiptRow) => string | null): CountEntry[] {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    const key = keyFn(row);
    if (!key) continue;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

const AUDIT_TRAIL_LIMIT = 200;

export async function getOpsReportSupplementalData(): Promise<{ floorControl: FloorControlData; auditTrail: AuditTrailEntry[] }> {
  const receipts = await getMaterialReceipts();

  const floorControl: FloorControlData = {
    exceptionsByStatus: countBy(receipts, r => EXCEPTION_STATUSES.includes(r.material_status) ? r.material_status : null),
    escalationsByTeam: countBy(receipts, r => r.escalated_to_team ? teamLabel(r.escalated_to_team) : null),
    intakeByOperator: countBy(receipts, r => r.intake_by),
    oversByStatus: countBy(receipts, r => r.overs_status),
  };

  // Capped so a large receipt population can't blow up the report's page count.
  const auditTrail: AuditTrailEntry[] = receipts
    .flatMap(r => (r.note_log ?? []).map(n => ({
      ts: n.timestamp,
      actor: n.author,
      action: n.kind ?? 'note',
      target: r.scan_number,
      details: n.text,
    })))
    .sort((a, b) => b.ts.localeCompare(a.ts))
    .slice(0, AUDIT_TRAIL_LIMIT);

  return { floorControl, auditTrail };
}
